import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Button,
  Stack,
  Divider,
  Paper,
  Chip,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  IconButton,
  AppBar,
  Toolbar,
  Container,
  Badge,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ThemedPaper from '../core/ThemedPaper';
import { useAppDispatch, useAppSelector } from '@web-client/state/store';
import { selectIdToken } from '@web-client/state/slices/user';
import Paths from '@web-client/paths';
import ApiService from '@web-client/services/ApiService';
import { useApiWithGlobalLoading } from '@web-client/hooks/useApiWithLoading';
import { CreatorProfile, CreatorProduct, CreatorPost } from '@src/other/classes';

// API functions defined outside component to prevent recreation
const fetchCreatorProfile = async (creatorId: string) => {
  const response = await ApiService.getInstance().get(`/api/v1/user/${creatorId}/profile`);
  return response?.result;
};

const fetchCreatorProducts = async (creatorId: string) => {
  const response = await ApiService.getInstance().get(`/api/v1/user/${creatorId}/courses`);
  return response?.results;
};

const fetchCreatorSubscription = async (creatorId: string) => {
  try {
    const response = await ApiService.getInstance().getUserSubscription(creatorId);
    return response;
  } catch (err) {
    return null;
  }
};

const fetchCreatorPosts = async (creatorId: string) => {
  const response = await ApiService.getInstance().get(`/api/v1/user/${creatorId}/posts`);
  return response?.results;
};


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`creator-tabpanel-${index}`}
      aria-labelledby={`creator-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const CreatorSummary: React.FC = () => {
  const { creatorId } = useParams<{ creatorId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const idToken = useAppSelector(selectIdToken);

  const [creatorProfile, setCreatorProfile] = useState<CreatorProfile | null>(null);
  const [creatorProducts, setCreatorProducts] = useState<CreatorProduct[]>([]);
  const [creatorPosts, setCreatorPosts] = useState<CreatorPost[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // API calls with automatic loading management
  const profileApi = useApiWithGlobalLoading(fetchCreatorProfile);
  const productsApi = useApiWithGlobalLoading(fetchCreatorProducts);
  const subscriptionApi = useApiWithGlobalLoading(fetchCreatorSubscription);
  const postsApi = useApiWithGlobalLoading(fetchCreatorPosts);

  useEffect(() => {
    if (!creatorId) return;

    const fetchCreatorData = async () => {
      try {
        setError(null);

        // Fetch creator profile
        const profile = await profileApi.execute(creatorId);
        if (profile) {
          setCreatorProfile(profile);
        }

        // Fetch creator products
        const products = await productsApi.execute(creatorId);
        if (products) {
          setCreatorProducts(products);
        }

        // Fetch creator posts
        const posts = await postsApi.execute(creatorId);
        if (posts) {
          setCreatorPosts(posts);
        }

        // Check if current user is subscribed to this creator
        if (idToken) {
          const subscription = await subscriptionApi.execute(creatorId);
          setIsSubscribed(!!subscription);

          // Check if current user is following this creator
          try {
            const followingResponse = await ApiService.getInstance().getFollowStatus(creatorId);
            setIsFollowing(!!followingResponse);
          } catch (err) {
            // User is not following
            setIsFollowing(false);
          }

          // Get follower count
          try {
            const followerCount = await ApiService.getInstance().getFollowerCount(creatorId);
            setFollowerCount(followerCount || 0);
          } catch (err) {
            setFollowerCount(0);
          }
        }
      } catch (err) {
        console.error('Error fetching creator data:', err);
        setError('Failed to load creator information');
      }
    };

    fetchCreatorData();
  }, [creatorId, idToken]);

  const handleSubscribe = () => {
    if (creatorId) {
      navigate(Paths.SUBSCRIBE.replace(':creatorId', creatorId));
    }
  };

  const handleProductClick = (productId: string) => {
    navigate(Paths.PRODUCT_DETAILS.replace(':productId', productId));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleUnsubscribe = () => {
    if (creatorId) {
      // TODO: Implement unsubscribe functionality
      console.log('Unsubscribe from creator:', creatorId);
      handleMenuClose();
    }
  };

    const handleFollow = async () => {
    if (!creatorId || !idToken || isFollowLoading) return;

    setIsFollowLoading(true);
    try {
      if (isFollowing) {
        // Unfollow
        await ApiService.getInstance().unfollowCreator(creatorId);
        setIsFollowing(false);
        setFollowerCount(prev => Math.max(0, prev - 1));
      } else {
        // Follow
        await ApiService.getInstance().followCreator(creatorId);
        setIsFollowing(true);
        setFollowerCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error following/unfollowing:', error);
      // TODO: Show error message to user
    } finally {
      setIsFollowLoading(false);
    }
  };

  // Check if any API call is loading
  const isLoading = profileApi.loading || productsApi.loading || subscriptionApi.loading || postsApi.loading;

  // Debug: Log the products array to check for missing IDs
  React.useEffect(() => {
    if (creatorProducts.length > 0) {
      console.log('Creator products:', creatorProducts);
      const productsWithoutId = creatorProducts.filter(p => !p.id);
      if (productsWithoutId.length > 0) {
        console.warn('Products without ID:', productsWithoutId);
      }
    }
  }, [creatorProducts]);

  if (isLoading) {
    return (
      <Box sx={{ padding: 3 }}>
        <Typography variant="h6">
          Loading creator profile...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: 3 }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!creatorProfile) {
    return (
      <Box sx={{ padding: 3 }}>
        <Typography variant="h6" color="error">
          Creator not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header Banner - OnlyFans Style */}
      <Box
        sx={{
          height: 300,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-end',
          pb: 4,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="flex-end">
            <Grid item>
              <Avatar
                src={creatorProfile.photoURL}
                sx={{
                  width: 120,
                  height: 120,
                  border: `4px solid ${theme.palette.background.paper}`,
                  boxShadow: theme.shadows[8],
                }}
              >
                {creatorProfile.displayName?.charAt(0) || creatorProfile.email?.charAt(0) || '?'}
              </Avatar>
            </Grid>
            <Grid item xs>
              <Stack spacing={2}>
                <Typography variant="h3" sx={{ color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
                  {creatorProfile.displayName || 'Anonymous Creator'}
                </Typography>
                {creatorProfile.bio && (
                  <Typography variant="h6" sx={{ color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
                    {creatorProfile.bio}
                  </Typography>
                )}
                <Stack direction="row" spacing={2} alignItems="center">
                  {!isSubscribed ? (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<PersonAddIcon />}
                        onClick={handleSubscribe}
                        size="large"
                        sx={{
                          bgcolor: 'white',
                          color: theme.palette.primary.main,
                          '&:hover': {
                            bgcolor: 'grey.100',
                          },
                        }}
                      >
                        Subscribe
                      </Button>
                      <Button
                        variant={isFollowing ? "contained" : "outlined"}
                        color={isFollowing ? "primary" : "inherit"}
                        onClick={handleFollow}
                        disabled={isFollowLoading}
                        size="large"
                        sx={{
                          borderColor: 'white',
                          color: isFollowing ? 'white' : 'white',
                          bgcolor: isFollowing ? 'rgba(255,255,255,0.2)' : 'transparent',
                          '&:hover': {
                            borderColor: 'white',
                            bgcolor: isFollowing ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
                          },
                        }}
                      >
                        {isFollowLoading ? '...' : (isFollowing ? '✓ Following' : '+ Follow')}
                      </Button>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Chip
                        icon={<FavoriteIcon />}
                        label="Subscribed"
                        color="success"
                        variant="filled"
                        sx={{ bgcolor: 'white', color: 'success.main' }}
                      />
                      <Button
                        variant={isFollowing ? "contained" : "outlined"}
                        color={isFollowing ? "primary" : "inherit"}
                        onClick={handleFollow}
                        disabled={isFollowLoading}
                        size="large"
                        sx={{
                          borderColor: 'white',
                          color: isFollowing ? 'white' : 'white',
                          bgcolor: isFollowing ? 'rgba(255,255,255,0.2)' : 'transparent',
                          '&:hover': {
                            borderColor: 'white',
                            bgcolor: isFollowing ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
                          },
                        }}
                      >
                        {isFollowLoading ? '...' : (isFollowing ? '✓ Following' : '+ Follow')}
                      </Button>
                      <IconButton
                        onClick={handleMenuOpen}
                        sx={{ color: 'white' }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                      >
                        <MenuItem onClick={handleUnsubscribe}>
                          Unsubscribe
                        </MenuItem>
                        <MenuItem onClick={handleMenuClose}>
                          View Subscription
                        </MenuItem>
                        <MenuItem onClick={handleMenuClose}>
                          Contact Creator
                        </MenuItem>
                      </Menu>
                    </Stack>
                  )}
                  <Stack direction="row" spacing={1}>
                    <Chip
                      icon={<AutoStoriesIcon />}
                      label={`${creatorProducts.length} courses`}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.3)',
                      }}
                    />
                    <Chip
                      icon={<PersonAddIcon />}
                      label={`${followerCount} followers`}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.3)',
                      }}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Content Tabs - OnlyFans Style */}
      <Container maxWidth="lg" sx={{ mt: -2, position: 'relative', zIndex: 1 }}>
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <AppBar position="static" color="default" elevation={0}>
            <Toolbar sx={{ px: 0 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ flexGrow: 1 }}
              >
                               <Tab label="Posts" />
               <Tab label="Courses" />
               <Tab label="About" />
              </Tabs>
            </Toolbar>
          </AppBar>

          {/* Posts Tab */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" gutterBottom>
              Recent Posts
            </Typography>
            {creatorPosts.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No posts yet
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={3}>
                {creatorPosts.map((post) => (
                  <Paper key={post.id} sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      {/* Post Header */}
                      <Stack direction="row" spacing={2} alignItems="center">
                        {post.isPinned && (
                          <Chip
                            label="Pinned"
                            color="primary"
                            size="small"
                            icon={<span>📌</span>}
                          />
                        )}
                        <Chip
                          label={post.type.replace('_', ' ')}
                          color="secondary"
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={post.visibility}
                          color="default"
                          size="small"
                          variant="outlined"
                        />
                      </Stack>

                      {/* Post Title */}
                      <Typography variant="h6">
                        {post.title}
                      </Typography>

                      {/* Post Content */}
                      <Typography variant="body1">
                        {post.content}
                      </Typography>

                      {/* Post Footer */}
                      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            ❤️ {post.likes}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            💬 {post.replies}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(post.createdAt).toLocaleDateString()}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </TabPanel>

          {/* Courses Tab */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom>
              Courses by {creatorProfile.displayName || 'this creator'}
            </Typography>
            {creatorProducts.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  No courses published yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This creator hasn't published any courses yet.
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {creatorProducts.map((product, index) => {
                  // Ensure we have a unique key, fallback to index if no ID
                  const uniqueKey = product.id || `product-${index}`;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={uniqueKey}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: theme.shadows[8],
                          },
                        }}
                        onClick={() => handleProductClick(product.id)}
                      >
                        <CardContent>
                          <Stack spacing={2}>
                            <Typography variant="h6" noWrap>
                              {product.title}
                            </Typography>
                            {product.description && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                }}
                              >
                                {product.description}
                              </Typography>
                            )}
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                label={product.isPublished ? 'Published' : 'Draft'}
                                color={product.isPublished ? 'success' : 'default'}
                                size="small"
                              />
                              {product.publishedAt && (
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(product.publishedAt).toLocaleDateString()}
                                </Typography>
                              )}
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </TabPanel>

          {/* About Tab */}
          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" gutterBottom>
              About {creatorProfile.displayName || 'this creator'}
            </Typography>
            <Paper sx={{ p: 3 }}>
              {creatorProfile.bio ? (
                <Typography variant="body1">
                  {creatorProfile.bio}
                </Typography>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No bio available.
                </Typography>
              )}
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Member since:</strong> {creatorProfile.createdAt ?
                    new Date(creatorProfile.createdAt).toLocaleDateString() : 'Unknown'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Total courses:</strong> {creatorProducts.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Followers:</strong> {followerCount.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Posts:</strong> {creatorPosts.length}
                </Typography>
              </Stack>
            </Paper>
          </TabPanel>


        </Paper>
      </Container>
    </Box>
  );
};

export default CreatorSummary;
